import React, { useState, useEffect, useRef } from "react";
import { authFetchInit, hasAuthSession } from "../utils/authSession";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Chip,
  Avatar,
  Collapse,
  Badge,
  Tooltip,
  LinearProgress,
  ButtonBase,
} from "@mui/material";
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachFile as AttachFileIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { API_ROOT } from "../config/apiConfig";
import type { SSOPrefillData } from "../types";
import { canRunVysorWizard, type WizardFlow } from "../utils/vysorFlows";
import {
  fetchVysorContext,
  type VysorAppCatalogItem,
  type VysorIdpOption,
  type VysorQuickAction,
} from "../utils/vysorCatalog";
import { mapQuickActionsToUi } from "../utils/vysorQuickActions";
import {
  advanceVysorWizard,
  buildWizardListPickerMessage,
  buildWizardOptions,
  isCreateInstanceIntent,
  isWizardChipListField,
  type WizardState,
} from "../utils/vysorWizardEngine";
import {
  runWizardAssistGate,
  isAssistCapableWizardField,
  enterWizardHelpMode,
} from "../utils/vysorWizardAssistGates";
import { processWizardAgentChatResponse } from "../utils/vysorWizardAgentBridge";
import {
  buildVysorWizardContext,
  beginPostSetupAppInstanceChain,
  enrichWizardMetadataField,
  handleVysorGuidedAction,
  processInstanceSubFlowStep,
} from "../utils/vysorWizardUi";
import {
  buildAgentContextHeader,
  looksLikeOpenAiConversation,
} from "../utils/vysorWizardAssistCommon";
import { vysorAiGoalOptionId, vysorAiGoalPrompt } from "../utils/vysorAiMode";
import { fetchUrlContentForVysor } from "../utils/vysorMetadata";
import {
  submitIgaEntitlementWizard,
  submitSetupAppWizard,
  submitSsoConfigWizard,
  wizardSubmitErrorMessage,
  wizardSuccessRoute,
} from "../utils/vysorWizardSubmit";
import {
  assistantPrefillSubmitError,
  canSubmitSsoPrefill,
  submitSsoFromAssistantPrefill,
} from "../utils/vysorAssistantPrefill";
import {
  continueChatFollowUpMessage,
  isContinueChatChip,
  planSsoSubmitChip,
} from "../utils/vysorChatSsoChips";
import { renderVysorBotContent } from "../utils/vysorChatRender";
import {
  classifyVysorMessageScope,
  getVysorOutOfScopeReply,
} from "../utils/vysorMessageScope";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  type: "user" | "bot";
  content: string;
  timestamp: Date;
}

interface QuickOption {
  id: string;
  label: string;
  description?: string;
  value?: string;
}

// ─── Component ───────────────────────────────────────────────────────────────

const TurboChat: React.FC = () => {
  const { user } = useAuth();
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === "dark";
  const chatSurface = isDark ? "background.paper" : "#fff";
  const chatCanvas = isDark ? "rgba(255,255,255,0.03)" : "#fafafa";
  const chatBorder = isDark ? "rgba(255,255,255,0.12)" : "#e0e0e0";
  const chatBorderSoft = isDark ? "rgba(255,255,255,0.08)" : "#eee";
  const botBubble = isDark ? "rgba(255,255,255,0.06)" : "white";
  const botShadow = isDark
    ? "0 2px 8px rgba(0,0,0,0.35)"
    : "0 2px 8px rgba(0,0,0,0.08)";
  const navigate = useNavigate();

  // Core chat state
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  // Conversation persistence
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Quick-reply chips
  const [currentOptions, setCurrentOptions] = useState<QuickOption[] | null>(
    null,
  );

  // File attachment
  const [attachedDoc, setAttachedDoc] = useState<{
    name: string;
    text: string;
  } | null>(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Deterministic wizard state (Setup application, IGA entitlement, SSO)
  const [wizard, setWizard] = useState<WizardState | null>(null);
  const [appCatalog, setAppCatalog] = useState<VysorAppCatalogItem[]>([]);
  const [configuredIdps, setConfiguredIdps] = useState<VysorIdpOption[]>([]);
  const [roleQuickActions, setRoleQuickActions] = useState<VysorQuickAction[]>(
    [],
  );
  const wizardRef = useRef<WizardState | null>(null);

  const [pendingSsoPrefill, setPendingSsoPrefill] =
    useState<SSOPrefillData | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && user && messages.length === 0) {
      loadWelcomeMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  // ─── Welcome message ───────────────────────────────────────────────────────

  const loadWelcomeMessage = async () => {
    try {
      if (!hasAuthSession()) return;

      setIsTyping(true);
      const [ctx, response] = await Promise.all([
        fetchVysorContext().catch(() => null),
        fetch(`${API_ROOT}/assistant/quick-start`, authFetchInit()),
      ]);
      if (ctx) {
        setAppCatalog(ctx.applications);
        setConfiguredIdps(ctx.configuredIdps || []);
        setRoleQuickActions(ctx.quickActions);
      }

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const welcomeData = result.data;
          addBotMessage(welcomeData.message);
          if (welcomeData.conversationId) {
            setConversationId(welcomeData.conversationId);
          }
          if (welcomeData.options && welcomeData.options.length > 0) {
            setCurrentOptions(welcomeData.options);
          } else if (
            welcomeData.next_questions &&
            welcomeData.next_questions.length > 0
          ) {
            setCurrentOptions(
              welcomeData.next_questions.map((q: string, i: number) => ({
                id: `q_${i}`,
                label: q,
              })),
            );
          }
          if (welcomeData.quickActions?.length) {
            setRoleQuickActions(welcomeData.quickActions);
          }
        }
      } else {
        addBotMessage("I couldn't start VYSOR just now. Refresh and try again.");
      }
    } catch (error) {
      addBotMessage("I couldn't start VYSOR just now. Refresh and try again.");
    } finally {
      setIsTyping(false);
    }
  };

  // ─── Message helpers ───────────────────────────────────────────────────────

  const addBotMessage = (content: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `bot_${Date.now()}_${Math.random()}`,
        type: "bot",
        content,
        timestamp: new Date(),
      },
    ]);
  };

  const addUserMessage = (content: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `user_${Date.now()}_${Math.random()}`,
        type: "user",
        content,
        timestamp: new Date(),
      },
    ]);
  };

  // ─── File upload ──────────────────────────────────────────────────────────

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadProgress(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${API_ROOT}/assistant/upload-doc`,
        authFetchInit({
          method: "POST",
          body: formData,
        }),
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.text) {
          setAttachedDoc({ name: file.name, text: result.text });
        } else {
          addBotMessage(
            "Sorry, I had trouble reading that file. Please try a different format.",
          );
        }
      } else {
        addBotMessage("File upload failed. Please try again.");
      }
    } catch (err) {
      addBotMessage("File upload failed. Please try again.");
    } finally {
      setUploadProgress(false);
      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ─── URL fetch ────────────────────────────────────────────────────────────

  const fetchUrlAndSend = async (url: string) => {
    try {
      setIsTyping(true);
      const { text } = await fetchUrlContentForVysor(url);
      await sendToApi(url, text || undefined, url);
    } catch (err: unknown) {
      setIsTyping(false);
      const msg = err instanceof Error ? err.message : "unknown error";
      addBotMessage(
        `I had trouble fetching that URL (${msg}). Try pasting the XML/metadata content directly, or use the metadata URL field in the SSO form.`,
      );
    }
  };

  // ─── Core send logic ──────────────────────────────────────────────────────

  const sendToApi = async (
    userMessage: string,
    attachmentText?: string,
    attachmentName?: string,
    optionId?: string,
    ssoPrefillPayload?: SSOPrefillData | null,
  ): Promise<boolean> => {
    let wizardResumed = false;
    try {
      if (!hasAuthSession()) {
        addBotMessage(
          "I need you to be logged in to help you. Please log in and try again.",
        );
        setIsTyping(false);
        return false;
      }

      if (
        classifyVysorMessageScope(userMessage, {
          attachmentText,
          optionId,
          catalogNames: appCatalog.map((a) => a.name),
          sessionMessages: messages.slice(-12).map((m) => ({
            role: m.type === "user" ? "user" : "assistant",
            content: m.content,
          })),
          midActiveFlow: Boolean(
            wizard ||
              pendingSsoPrefill ||
              (conversationId && messages.length > 1),
          ),
        }) === "blocked"
      ) {
        addBotMessage(getVysorOutOfScopeReply());
        setIsTyping(false);
        return false;
      }

      const body: Record<string, any> = {
        message: userMessage,
      };
      if (conversationId) body.conversationId = conversationId;
      if (attachmentText) body.attachmentText = attachmentText;
      if (attachmentName) body.attachmentName = attachmentName;
      if (optionId) body.optionId = optionId;
      if (ssoPrefillPayload) body.ssoPrefill = ssoPrefillPayload;

      const response = await fetch(
        `${API_ROOT}/assistant/chat`,
        authFetchInit({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          const data = result.data;

          // Persist conversation ID (top-level on chat responses)
          if (result.conversationId) {
            setConversationId(result.conversationId);
          }

          const inWizard = Boolean(wizardRef.current);

          if (data.prefill || data.message) {
            const agentResult = processWizardAgentChatResponse(
              data,
              wizardRef.current,
              appCatalog,
              wizardContext(),
              {
                setWizard,
                addBotMessage: (text, options) => {
                  addBotMessage(text);
                  if (options?.length) {
                    setCurrentOptions(
                      options.map((o) => ({
                        id: o.id,
                        label: o.label,
                        description: o.description,
                        value: o.value,
                      })),
                    );
                  }
                },
                showWizardField: (field, collected) =>
                  applyWizardOptions(field, collected),
              },
              canSubmitSsoPrefill,
            );
            if (agentResult.wizardResumed) {
              wizardResumed = true;
              if (agentResult.advanceAnswer) {
                void advanceWizard(agentResult.advanceAnswer);
              }
              return wizardResumed;
            }
            if (agentResult.pendingSsoPrefill) {
              setPendingSsoPrefill(agentResult.pendingSsoPrefill);
              return wizardResumed;
            }
          } else if (!inWizard) {
            setCurrentOptions(data.options || null);
            addBotMessage(
              "I hit a snag finishing that reply. Tell me what you need next — or pick a quick action.",
            );
          } else {
            addBotMessage(
              "I hit a snag finishing that reply. Tell me what you need next — or pick a quick action.",
            );
          }
        } else {
          const errText =
            (typeof result?.error === "string" && result.error) ||
            "I encountered an error processing your request. Try **list pending SSO** or a quick action.";
          addBotMessage(errText);
        }
      } else {
        let errText = `I'm having trouble connecting right now (HTTP ${response.status}). Please try again in a moment.`;
        try {
          const body = await response.json();
          if (typeof body?.error === "string" && body.error) {
            errText = `I couldn't complete that: ${body.error}. Try **list pending SSO** or a quick action.`;
          } else if (body?.success && body?.data?.message) {
            // Backend may return 200-style recovery even on older error shapes
            addBotMessage(body.data.message);
            if (Array.isArray(body.data.options) && body.data.options.length) {
              setCurrentOptions(
                body.data.options.map(
                  (o: {
                    id: string;
                    label: string;
                    description?: string;
                    value?: string;
                  }) => ({
                    id: o.id,
                    label: o.label,
                    description: o.description,
                    value: o.value,
                  }),
                ),
              );
            }
            return false;
          }
        } catch {
          /* keep default */
        }
        addBotMessage(errText);
      }
    } catch (error) {
      const detail =
        error instanceof Error && error.message ? error.message : "";
      addBotMessage(
        detail
          ? `I encountered an error: ${detail}. Try again or say **list pending SSO**.`
          : "I encountered an error. Try again or say **list pending SSO**.",
      );
    } finally {
      setIsTyping(false);
    }
    return wizardResumed;
  };

  // ─── Wizard helpers ───────────────────────────────────────────────────────

  const wizardContext = () => ({
    ...buildVysorWizardContext(user?.role, configuredIdps),
    listPage: wizard?.listPage ?? wizard?.catalogPage,
    listFilter: wizard?.listFilter ?? wizard?.catalogFilter,
    catalogPage: wizard?.listPage ?? wizard?.catalogPage,
    catalogFilter: wizard?.listFilter ?? wizard?.catalogFilter,
  });

  const applyWizardOptions = (
    field: import("../utils/vysorFlows").WizardField,
    collected: Record<string, string>,
    opts?: { withQuestion?: boolean },
  ) => {
    if (opts?.withQuestion) {
      if (isWizardChipListField(field)) {
        addBotMessage(
          buildWizardListPickerMessage(
            field,
            appCatalog,
            collected,
            wizardContext(),
            wizard?.listPage ?? wizard?.catalogPage ?? 1,
            wizard?.listFilter ?? wizard?.catalogFilter,
          ),
        );
      } else {
        const hint = field.hint ? `\n\n${field.hint}` : "";
        addBotMessage(`${field.question}${hint}`);
      }
    }
    const options: QuickOption[] = buildWizardOptions(
      field,
      appCatalog,
      collected,
      wizardContext(),
    ).map((o) => ({
      id: o.id,
      label: o.label,
      description: o.description,
      value: o.value,
    }));
    setCurrentOptions(options);
  };

  const refreshCatalog = async () => {
    const ctx = await fetchVysorContext();
    setAppCatalog(ctx.applications);
    setConfiguredIdps(ctx.configuredIdps || []);
    return ctx.applications;
  };

  useEffect(() => {
    wizardRef.current = wizard;
  }, [wizard]);

  const startWizard = (flow: WizardFlow) => {
    if (!canRunVysorWizard(user?.role, flow.id)) {
      setIsMinimized(false);
      setIsOpen(true);
      addBotMessage(
        "Only platform administrators can register new applications. You can still configure SSO or manage entitlements for apps already assigned to you.",
      );
      return;
    }
    setIsMinimized(false);
    setIsOpen(true);
    void (async () => {
      let catalog = appCatalog;
      if (
        flow.fields.some(
          (f) => f.type === "application" || f.type === "instance",
        )
      ) {
        try {
          catalog = await refreshCatalog();
        } catch {
          /* keep existing catalog */
        }
      }
      const collected: Record<string, string> = {};
      setWizard({ flow, stepIndex: 0, collected, listPage: 1 });
      addBotMessage(flow.intro);
      const firstField = flow.fields[0];
      const ctx = {
        configuredIdps,
        canSetupApp: canRunVysorWizard(user?.role, "setup-app"),
        listPage: 1,
      };
      if (isWizardChipListField(firstField)) {
        addBotMessage(
          buildWizardListPickerMessage(firstField, catalog, collected, ctx, 1),
        );
      } else {
        const hint = firstField.hint ? `\n\n${firstField.hint}` : "";
        addBotMessage(`${firstField.question}${hint}`);
      }
      const options = buildWizardOptions(firstField, catalog, collected, ctx);
      setCurrentOptions(
        options.map((o) => ({
          id: o.id,
          label: o.label,
          description: o.description,
          value: o.value,
        })),
      );
    })();
  };

  const cancelWizard = () => {
    setWizard(null);
    setCurrentOptions(null);
    addBotMessage(
      "Cancelled. Pick another quick action above when you're ready.",
    );
  };

  const advanceWizard = async (
    rawAnswer: string,
    optionId?: string,
    optionValue?: string,
    attachmentText?: string,
  ) => {
    if (!wizard) return;

    let activeWizard = wizard;

    if (activeWizard.subFlow === "add-instance") {
      const handled = await processInstanceSubFlowStep(
        activeWizard,
        rawAnswer,
        optionId,
        optionValue,
        {
          setWizard,
          addBotMessage,
          applyWizardOptions: (field, collected, opts) => {
            if (opts?.withQuestion) {
              const hint = field.hint ? `\n\n_${field.hint}_` : "";
              addBotMessage(`${field.question}${hint}`);
            }
            applyWizardOptions(field, collected);
          },
          onRefreshCatalog: refreshCatalog,
          setBusy: setIsTyping,
        },
      );
      if (handled) return;
    }

    const currentField = activeWizard.flow.fields[activeWizard.stepIndex];
    let result:
      | import("../utils/vysorWizardEngine").WizardAdvanceResult
      | null = null;

    const gate = await runWizardAssistGate(
      activeWizard,
      rawAnswer,
      optionId,
      optionValue,
      appCatalog,
      wizardContext(),
      {
        setWizard,
        addBotMessage: (text, options) => {
          addBotMessage(text);
          if (options?.length) {
            setCurrentOptions(
              options.map((o) => ({
                id: o.id,
                label: o.label,
                description: o.description,
                value: o.value,
              })),
            );
          }
        },
        showField: (field, collected) => applyWizardOptions(field, collected),
        callAgent: async (contextMessage, refText) => {
          setIsTyping(true);
          try {
            const mergedAttachment = [refText, attachmentText]
              .filter(Boolean)
              .join("\n\n")
              .trim();
            return await sendToApi(
              contextMessage,
              mergedAttachment || undefined,
            );
          } finally {
            setIsTyping(false);
          }
        },
        getWizard: () => wizardRef.current,
      },
      attachmentText,
    );
    if (gate.handled) return;
    activeWizard = gate.wizard;
    rawAnswer = gate.rawAnswer;
    optionId = gate.optionId;
    optionValue = gate.optionValue;

    const metadataInput = attachmentText?.trim() || rawAnswer;
    if (
      !result &&
      currentField.key === "metadata_or_notes" &&
      metadataInput.trim() &&
      !optionValue
    ) {
      setIsTyping(true);
      try {
        const enriched = await enrichWizardMetadataField(
          activeWizard.collected,
          metadataInput,
        );
        if (enriched.error) addBotMessage(enriched.error);
        if (enriched.summary) addBotMessage(enriched.summary);
        if (enriched.abortAdvance) {
          applyWizardOptions(currentField, activeWizard.collected, {
            withQuestion: true,
          });
          return;
        }
        activeWizard = { ...activeWizard, collected: enriched.collected };
        setWizard(activeWizard);
        rawAnswer = enriched.collected.metadata_or_notes || metadataInput;
      } finally {
        setIsTyping(false);
      }
    }

    if (!result) {
      result = advanceVysorWizard(
        activeWizard,
        rawAnswer,
        optionId,
        optionValue,
        appCatalog,
        {
          ...wizardContext(),
          listPage: activeWizard.listPage ?? activeWizard.catalogPage,
          listFilter: activeWizard.listFilter ?? activeWizard.catalogFilter,
          catalogPage: activeWizard.listPage ?? activeWizard.catalogPage,
          catalogFilter: activeWizard.listFilter ?? activeWizard.catalogFilter,
        },
      );
    }
    if (!result) return;
    const wizardResult = result;

    if (wizardResult.kind === "cancelled") {
      cancelWizard();
      return;
    }

    if (wizardResult.kind === "repaint-options" && wizardResult.nextField) {
      const patched: WizardState = {
        ...activeWizard,
        ...(wizardResult.wizardPatch || {}),
        collected: wizardResult.collected || activeWizard.collected,
      };
      setWizard(patched);
      const msg =
        wizardResult.pageMessage ||
        (patched.awaitingListFilter || patched.awaitingCatalogFilter
          ? "Type part of a name to filter this list."
          : wizardResult.nextField.question);
      addBotMessage(msg);
      const options = buildWizardOptions(
        wizardResult.nextField,
        appCatalog,
        patched.collected,
        {
          ...wizardContext(),
          listPage: patched.listPage ?? patched.catalogPage,
          listFilter: patched.listFilter ?? patched.catalogFilter,
          catalogPage: patched.listPage ?? patched.catalogPage,
          catalogFilter: patched.listFilter ?? patched.catalogFilter,
        },
      );
      setCurrentOptions(
        options.map((o) => ({
          id: o.id,
          label: o.label,
          description: o.description,
          value: o.value,
        })),
      );
      return;
    }

    if (wizardResult.kind === "guided-action" && wizardResult.action) {
      await handleVysorGuidedAction(
        wizardResult.action,
        {
          onRefreshCatalog: refreshCatalog,
          onStartWizard: startWizard,
          onAiGoal: (flowId) => {
            void startAiGoal(
              flowId,
              flowId === "setup-app" ? "Add a new app" : flowId,
            );
          },
          onNavigate: (path) => {
            setIsOpen(false);
            navigate(path);
          },
          addBotMessage,
          setWizard,
          getWizard: () => wizardRef.current,
          applyWizardOptions,
        },
        wizardResult.collected,
      );
      return;
    }

    if (wizardResult.kind === "invalid") {
      const current = activeWizard.flow.fields[activeWizard.stepIndex];
      if (isAssistCapableWizardField(activeWizard) && rawAnswer.trim()) {
        const gateRetry = await runWizardAssistGate(
          enterWizardHelpMode(activeWizard),
          rawAnswer,
          optionId,
          optionValue,
          appCatalog,
          wizardContext(),
          {
            setWizard,
            addBotMessage: (text, options) => {
              addBotMessage(text);
              if (options?.length) {
                setCurrentOptions(
                  options.map((o) => ({
                    id: o.id,
                    label: o.label,
                    description: o.description,
                    value: o.value,
                  })),
                );
              }
            },
            showField: (field, collected) =>
              applyWizardOptions(field, collected),
            callAgent: async (contextMessage, refText) => {
              setIsTyping(true);
              try {
                const mergedAttachment = [refText, attachmentText]
                  .filter(Boolean)
                  .join("\n\n")
                  .trim();
                return await sendToApi(
                  contextMessage,
                  mergedAttachment || undefined,
                );
              } finally {
                setIsTyping(false);
              }
            },
            getWizard: () => wizardRef.current,
          },
          attachmentText,
        );
        if (gateRetry.handled) return;
      }
      const hint =
        wizardResult.invalidHint ||
        current.hint ||
        "Please choose one of the options below.";
      if (isWizardChipListField(current)) {
        addBotMessage(
          `${hint}\n\n${buildWizardListPickerMessage(
            current,
            appCatalog,
            activeWizard.collected,
            wizardContext(),
            activeWizard.listPage ?? activeWizard.catalogPage ?? 1,
            activeWizard.listFilter ?? activeWizard.catalogFilter,
          )}`,
        );
      } else {
        addBotMessage(hint);
      }
      applyWizardOptions(current, activeWizard.collected);
      return;
    }

    if (wizardResult.kind === "complete-sso-chat" && wizardResult.collected) {
      setWizard(null);
      setCurrentOptions(null);
      if (
        wizardResult.ssoPrefill?.applicationId &&
        wizardResult.ssoPrefill.instanceId
      ) {
        setIsTyping(true);
        try {
          const created = await submitSsoConfigWizard(
            wizardResult.collected,
            user?.email,
          );
          await refreshCatalog();
          const route = wizardSuccessRoute("configure-sso", user?.role);
          addBotMessage(
            `SSO configuration submitted for **${wizardResult.collected.application_name}** (${wizardResult.collected.instance_name}).` +
              (created.status ? ` Status: **${created.status}**.` : ""),
          );
          setIsOpen(false);
          navigate(route);
        } catch (err) {
          addBotMessage(
            `Could not submit SSO configuration: ${wizardSubmitErrorMessage(err, "Please try again.")}`,
          );
        } finally {
          setIsTyping(false);
        }
        return;
      }
      addBotMessage(
        "I need both an application and environment from your catalog before I can submit SSO. Start **Configure SSO** again.",
      );
      return;
    }

    if (
      wizardResult.kind === "complete-navigate" &&
      wizardResult.collected &&
      wizardResult.storagePayload
    ) {
      const flow = activeWizard.flow;
      const storagePayload = wizardResult.storagePayload;
      setWizard(null);
      setCurrentOptions(null);

      setIsTyping(true);
      try {
        if (flow.id === "setup-app") {
          const app = await submitSetupAppWizard(storagePayload, user?.id);
          await refreshCatalog();
          beginPostSetupAppInstanceChain(
            { id: app.id, name: app.name },
            {
              addBotMessage,
              setWizard,
              applyWizardOptions: (field, collected, opts) => {
                if (opts?.withQuestion) {
                  const hint = field.hint ? `\n\n_${field.hint}_` : "";
                  addBotMessage(`${field.question}${hint}`);
                }
                applyWizardOptions(field, collected);
              },
            },
          );
        } else if (flow.id === "iga-entitlements") {
          const appRisk = appCatalog.find(
            (a) => a.id === storagePayload.application_id,
          )?.risk_level;
          const ent = await submitIgaEntitlementWizard(storagePayload, appRisk);
          await refreshCatalog();
          addBotMessage(
            `Entitlement **${ent?.name || storagePayload.name}** was created.`,
          );
          setIsOpen(false);
          navigate(wizardSuccessRoute("iga-entitlements", user?.role));
        } else {
          addBotMessage(
            "This wizard should complete inside VYSOR. Please try the quick action again.",
          );
        }
      } catch (err) {
        addBotMessage(
          `Could not complete ${flow.title}: ${wizardSubmitErrorMessage(err, "Please try again.")}`,
        );
      } finally {
        setIsTyping(false);
      }
      return;
    }

    if (
      wizardResult.kind === "next" &&
      wizardResult.nextField &&
      wizardResult.nextStepIndex !== undefined &&
      wizardResult.collected
    ) {
      setWizard({
        flow: activeWizard.flow,
        stepIndex: wizardResult.nextStepIndex,
        collected: wizardResult.collected,
        listPage: 1,
      });
      const nextField = wizardResult.nextField;
      if (isWizardChipListField(nextField)) {
        addBotMessage(
          buildWizardListPickerMessage(
            nextField,
            appCatalog,
            wizardResult.collected,
            {
              ...wizardContext(),
              listPage: 1,
              listFilter: undefined,
            },
            1,
          ),
        );
      } else {
        const hint = nextField.hint ? `\n\n${nextField.hint}` : "";
        addBotMessage(`${nextField.question}${hint}`);
      }
      const options = buildWizardOptions(
        nextField,
        appCatalog,
        wizardResult.collected,
        {
          ...wizardContext(),
          listPage: 1,
          listFilter: undefined,
        },
      );
      setCurrentOptions(
        options.map((o) => ({
          id: o.id,
          label: o.label,
          description: o.description,
          value: o.value,
        })),
      );
    }
  };

  // ─── Handle send (main entry point) ──────────────────────────────────────

  const handleSendMessage = async (
    overrideMessage?: string,
    optionId?: string,
  ) => {
    const raw = overrideMessage !== undefined ? overrideMessage : inputValue;
    const userMessage = raw.trim();
    if (!userMessage) {
      if (wizard) {
        const field = wizard.flow.fields[wizard.stepIndex];
        addBotMessage(
          field.required
            ? "This field is required. Please enter a value or choose **Cancel**."
            : "Enter a value or choose **Skip** / **Cancel**.",
        );
      }
      return;
    }

    addUserMessage(userMessage);
    setInputValue("");
    setCurrentOptions(null);

    if (wizard) {
      let docText: string | undefined;
      if (attachedDoc) {
        docText = attachedDoc.text;
        setAttachedDoc(null);
      }
      if (
        !optionId &&
        isAssistCapableWizardField(wizard) &&
        looksLikeOpenAiConversation(userMessage) &&
        !isCreateInstanceIntent(userMessage) &&
        wizard.subFlow !== "add-instance"
      ) {
        const field = wizard.flow.fields[wizard.stepIndex];
        const contextMessage = buildAgentContextHeader(
          `Configure — ${wizard.flow.title}`,
          [
            `Current step: ${field.key} — ${field.question}`,
            `Application: ${wizard.collected.application_name || "not picked yet"}`,
            `Environment: ${wizard.collected.instance_name || "not picked yet"}`,
            "Answer the user's actual question dynamically. Do not use a fixed template.",
            "Do not restart the wizard. Stay on this step.",
            "",
            `User: ${userMessage}`,
          ],
        );
        await sendToApi(contextMessage, docText);
        const w = wizardRef.current;
        if (w) {
          const f = w.flow.fields[w.stepIndex];
          if (f) applyWizardOptions(f, w.collected, { withQuestion: false });
        }
        return;
      }
      await advanceWizard(userMessage, optionId, undefined, docText);
      return;
    }

    setIsTyping(true);

    // URL detection
    if (userMessage.match(/^https?:\/\//i)) {
      await fetchUrlAndSend(userMessage);
      return;
    }

    // File attachment
    let docText: string | undefined;
    let docName: string | undefined;
    if (attachedDoc) {
      docText = attachedDoc.text;
      docName = attachedDoc.name;
      setAttachedDoc(null);
    }

    await sendToApi(userMessage, docText, docName, optionId);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const startAiGoal = async (flowId: WizardFlow["id"], userLabel: string) => {
    if (!canRunVysorWizard(user?.role, flowId)) {
      addUserMessage(userLabel);
      setCurrentOptions(null);
      if (flowId === "setup-app") {
        addBotMessage(
          "You need an admin role to register a new application. Ask an admin, or choose **Configure SSO** if the app already exists.",
        );
      } else if (flowId === "configure-sso") {
        addBotMessage(
          "Your role can’t configure SSO in VYSOR. Ask an SSO admin or app owner, or pick another action you can access.",
        );
      } else {
        addBotMessage(
          "Your role can’t decide who can access apps in VYSOR. Ask an IGA admin or app owner, or pick another action you can access.",
        );
      }
      return;
    }
    setWizard(null);
    setCurrentOptions(null);
    addUserMessage(userLabel);
    setIsTyping(true);
    try {
      await sendToApi(
        vysorAiGoalPrompt(flowId),
        undefined,
        undefined,
        vysorAiGoalOptionId(flowId),
      );
    } catch (err: unknown) {
      const detail = err instanceof Error && err.message ? err.message : "";
      addBotMessage(
        detail
          ? `I ran into an error: ${detail}. Try again, or describe what you need in your own words.`
          : "I ran into an error. Describe what you need in your own words and I’ll help.",
      );
    } finally {
      setIsTyping(false);
    }
  };

  const handleChipClick = (opt: QuickOption | string) => {
    const label = typeof opt === "string" ? opt : opt.label;
    const optionId = typeof opt === "object" ? opt.id : undefined;
    const optionValue = typeof opt === "object" ? opt.value : undefined;

    // Prefer structural ids — never let label heuristics steal continue_sso
    if (optionId === "continue_sso") {
      handleSendMessage(label, optionId);
      return;
    }

    if (
      optionId === "start_setup_app" ||
      ((!optionId || optionId === "start_setup_app") &&
        (/^1\s*[—-]\s*Add a new app$/i.test(label) || /^Add a new app$/i.test(label)))
    ) {
      if (!canRunVysorWizard(user?.role, "setup-app")) {
        addUserMessage(label);
        setCurrentOptions(null);
        addBotMessage(
          "You need an admin role to register a new application. Ask an admin, or choose **Configure SSO** if the app already exists.",
        );
        return;
      }
      void startAiGoal("setup-app", label);
      return;
    }
    if (
      optionId === "start_configure_sso" ||
      ((!optionId || optionId === "start_configure_sso") &&
        (/^2\s*[—-]\s*Configure SSO$/i.test(label) ||
          /^(?:Configure|Set up) (?:SSO|company login)$/i.test(label) ||
          /^Configure SSO$/i.test(label) ||
          /^App already exists/i.test(label) ||
          /^Walk me through Configure SSO$/i.test(label)))
    ) {
      if (!canRunVysorWizard(user?.role, "configure-sso")) {
        addUserMessage(label);
        setCurrentOptions(null);
        addBotMessage(
          "SSO isn’t available for your role. Ask an admin or try another action.",
        );
        return;
      }
      void startAiGoal("configure-sso", label);
      return;
    }
    if (
      optionId === "start_iga_entitlements" ||
      ((!optionId || optionId === "start_iga_entitlements") &&
        (/^3\s*[—-]\s*IGA entitlements$/i.test(label) ||
          /^Who can access$/i.test(label) ||
          /^IGA entitlements$/i.test(label)))
    ) {
      if (!canRunVysorWizard(user?.role, "iga-entitlements")) {
        addUserMessage(label);
        setCurrentOptions(null);
        addBotMessage(
          "Who-can-access isn’t available for your role. Ask an admin, or try another action.",
        );
        return;
      }
      void startAiGoal("iga-entitlements", label);
      return;
    }

    if (
      (optionId === "submit_sso" ||
        optionId === "open_sso_form" ||
        label === "Submit SSO configuration" ||
        label === "Open SSO form")
    ) {
      const plan = planSsoSubmitChip({
        optionId,
        label,
        pendingSsoPrefill,
        hasConversation: Boolean(conversationId),
      });
      if (plan.mode === "direct_prefill") {
        const prefill = plan.prefill;
        setPendingSsoPrefill(null);
        setCurrentOptions(null);
        addUserMessage(label);
        setIsTyping(true);
        void (async () => {
          try {
            const created = await submitSsoFromAssistantPrefill(
              prefill,
              user?.email,
            );
            await refreshCatalog();
            addBotMessage(
              `SSO configuration submitted for **${prefill.applicationName || "your application"}**.` +
                (created.status ? ` Status: **${created.status}**.` : ""),
            );
            setIsOpen(false);
            navigate(wizardSuccessRoute("configure-sso", user?.role));
          } catch (err) {
            addBotMessage(
              `Could not submit SSO configuration: ${assistantPrefillSubmitError(err)}`,
            );
            setPendingSsoPrefill(prefill);
          } finally {
            setIsTyping(false);
          }
        })();
        return;
      }
      if (plan.mode === "agent") {
        setCurrentOptions(null);
        addUserMessage(label);
        setIsTyping(true);
        void sendToApi(
          label,
          undefined,
          undefined,
          plan.optionId,
          plan.ssoPrefill || pendingSsoPrefill,
        );
        return;
      }
    }
    if (isContinueChatChip(optionId, label)) {
      setPendingSsoPrefill(null);
      setCurrentOptions(null);
      addUserMessage(label);
      setIsTyping(true);
      void sendToApi(
        continueChatFollowUpMessage(label),
        undefined,
        undefined,
        "continue_chat",
      );
      return;
    }
    if (wizard) {
      // Agent SSO chips must not be treated as wizard field answers
      if (optionId === "submit_sso" || optionId === "open_sso_form") {
        addUserMessage(label);
        setIsTyping(true);
        void sendToApi(label, undefined, undefined, optionId);
        return;
      }
      addUserMessage(label);
      setCurrentOptions(null);
      void advanceWizard(label, optionId, optionValue);
      return;
    }
    handleSendMessage(label, optionId);
  };

  const quickActions = mapQuickActionsToUi(roleQuickActions, {
    onWizard: (flow) => {
      void startAiGoal(flow.id, flow.title);
    },
    onNavigate: (path) => {
      setIsOpen(false);
      navigate(path);
    },
    userRole: user?.role,
  });

  if (!user) return null;

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <Box>
          <Tooltip title="VYSOR — your guide for apps, SSO & access">
            <IconButton
              aria-label="Open VYSOR"
              onClick={() => setIsOpen(true)}
              sx={{
                color: "inherit",
              }}
            >
              <Badge
                invisible={messages.length === 0}
                badgeContent={messages.length}
                color="error"
              >
                <ChatIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Chat Window */}
      <Collapse in={isOpen} orientation="vertical">
        <Paper
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            width: 480,
            maxWidth: "calc(100vw - 32px)",
            height: isMinimized ? 60 : 580,
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            borderRadius: 2,
            overflow: "hidden",
            transition: "height 0.2s ease-in-out",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: colors.primary,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  mr: 1,
                  width: 32,
                  height: 32,
                }}
              >
                <BotIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  VYSOR
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Apps · SSO · access
                </Typography>
              </Box>
            </Box>
            <Box>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(!isMinimized)}
                sx={{ color: "white", mr: 1 }}
              >
                {isMinimized ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setIsOpen(false)}
                sx={{ color: "white" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {!isMinimized && (
            <>
              {/* Quick actions — always visible, deterministic shortcuts */}
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  gap: 0.75,
                  borderBottom: `1px solid ${chatBorderSoft}`,
                  backgroundColor: chatSurface,
                  overflowX: "auto",
                }}
              >
                {quickActions.map((qa) => (
                  <Tooltip
                    key={qa.id}
                    title={qa.description}
                    arrow
                    placement="top"
                  >
                    <ButtonBase
                      onClick={qa.onClick}
                      sx={{
                        flex: "1 1 0",
                        minWidth: 0,
                        px: 1,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: `1px solid ${chatBorder}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        textAlign: "left",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          borderColor: qa.color,
                          backgroundColor: `${qa.color}10`,
                          transform: "translateY(-1px)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: `${qa.color}15`,
                          color: qa.color,
                          flexShrink: 0,
                        }}
                      >
                        {qa.icon}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            lineHeight: 1.1,
                            color: "text.primary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {qa.label}
                        </Typography>
                      </Box>
                    </ButtonBase>
                  </Tooltip>
                ))}
              </Box>

              {/* Messages */}
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  p: 2,
                  backgroundColor: chatCanvas,
                }}
              >
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      justifyContent:
                        message.type === "user" ? "flex-end" : "flex-start",
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "82%",
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor:
                          message.type === "user" ? colors.primary : "white",
                        color:
                          message.type === "user" ? "white" : "text.primary",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      }}
                    >
                      {message.type === "bot" ? (
                        renderVysorBotContent(message.content)
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}
                        >
                          {message.content}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: botBubble,
                        boxShadow: botShadow,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        VYSOR is typing…
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Quick-reply chips (after last bot message) */}
                {!isTyping && currentOptions && currentOptions.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 1.5,
                      pl: 0.5,
                    }}
                  >
                    {currentOptions.map((opt) => (
                      <Chip
                        key={opt.id}
                        label={opt.label}
                        size="small"
                        onClick={() => handleChipClick(opt)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: botBubble,
                          border: `1px solid ${colors.primary}`,
                          color: colors.primary,
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          "&:hover": {
                            backgroundColor: colors.primary,
                            color: "white",
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </Box>

              {/* Attached doc chip */}
              {attachedDoc && (
                <Box
                  sx={{
                    px: 2,
                    pt: 1,
                    borderTop: `1px solid ${chatBorder}`,
                    backgroundColor: chatCanvas,
                  }}
                >
                  <Chip
                    icon={<AttachFileIcon />}
                    label={attachedDoc.name}
                    size="small"
                    onDelete={() => setAttachedDoc(null)}
                    sx={{
                      backgroundColor: "#e8f4fd",
                      color: colors.primary,
                      maxWidth: "100%",
                    }}
                  />
                </Box>
              )}

              {/* Upload progress */}
              {uploadProgress && <LinearProgress sx={{ mx: 2 }} />}

              {/* Input area */}
              <Box
                sx={{
                  p: 2,
                  borderTop: attachedDoc ? "none" : `1px solid ${chatBorder}`,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {/* Paperclip button */}
                  <Tooltip title="Attach a document (PDF, DOCX, TXT, XML, MD)">
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isTyping || uploadProgress}
                      sx={{ color: colors.primary, flexShrink: 0 }}
                    >
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {/* URL hint button */}
                  <Tooltip title="Paste a metadata URL or documentation URL to auto-detect SSO config">
                    <IconButton
                      size="small"
                      disabled
                      sx={{ color: "text.disabled", flexShrink: 0 }}
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ask VYSOR — setup, SSO, access…"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isTyping || uploadProgress}
                    multiline
                    maxRows={3}
                  />
                  <IconButton
                    onClick={() => handleSendMessage()}
                    disabled={!inputValue.trim() || isTyping || uploadProgress}
                    sx={{ color: colors.primary, flexShrink: 0 }}
                  >
                    <SendIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,.xml,.md"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
            </>
          )}
        </Paper>
      </Collapse>
    </>
  );
};

export default TurboChat;
